"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { getCoinBalance } from "@/lib/actions/coin-actions";
import { useAuth } from "@/providers/auth-provider";

/**
 * In-flight request dedupe, keyed by user id. When several components mount
 * together (e.g. the desktop NavbarAuth and the mobile hamburger menu both
 * render in the same header), they share a single getCoinBalance() call
 * instead of each firing their own. The entry is cleared once the request
 * settles, so a later mount still fetches fresh — freshness is unchanged,
 * only the simultaneous duplicate is removed.
 */
const inflight = new Map<string, Promise<number>>();
function fetchBalance(userId: string): Promise<number> {
  let promise = inflight.get(userId);
  if (!promise) {
    promise = getCoinBalance().then(data => data.balanceCoins).finally(() => {
      inflight.delete(userId);
    });
    inflight.set(userId, promise);
  }
  return promise;
}

/**
 * Returns the authenticated user's coin balance, or `null` while loading /
 * signed out. Shared across header components via the in-flight dedupe above.
 */
export function useCoinBalance() {
  const $ = _c(3);
  const {
    user
  } = useAuth();
  const [coins, setCoins] = useState(null);
  let t0;
  let t1;
  if ($[0] !== user) {
    t0 = () => {
      if (!user) {
        return;
      }
      let active = true;
      fetchBalance(user.id).then(balance => {
        if (active) {
          setCoins(balance);
        }
      }).catch(() => {
        if (active) {
          setCoins(null);
        }
      });
      return () => {
        active = false;
      };
    };
    t1 = [user];
    $[0] = user;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return coins;
}